// src/components/common/ErrorBoundary.jsx
import React from "react";
import { Box, Typography, Button, Paper, Stack } from "@mui/material";
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isResolved: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isResolved: true,
    });
    // Optional: reload the page or reset the component tree
    // window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            p: 3,
            backgroundColor: "#f5f5f5",
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 500,
              width: "100%",
              textAlign: "center",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 64, color: "#ff4d4f" }} />
            </Box>

            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Something went wrong
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We apologize for the inconvenience. Please try refreshing the page
              or go back.
            </Typography>

            {this.state.error && (
              <Box
                sx={{
                  bgcolor: "#fff1f0",
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                  textAlign: "left",
                  overflow: "auto",
                  maxHeight: 100,
                }}
              >
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontFamily: "monospace" }}
                >
                  {this.state.error.toString()}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a67d8 0%, #6b46a1 100%)",
                  },
                }}
              >
                Try Again
              </Button>
              <Button variant="outlined" onClick={this.handleGoBack}>
                Go Back
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 3, display: "block" }}
            >
              If the problem persists, please contact support.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ✅ Default export
export default ErrorBoundary;
